import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DidEmbed from '../components/DidEmbed';
import { Toaster } from "@/components/ui/sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mic, Sparkles, BookOpen, ArrowLeft, AlertCircle, Rocket, Bell, Zap, Users as UsersIcon, Code2, Coffee, Brain, FileText, MessageSquare, Target, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { SessionRecorder } from '../components/SessionRecorder';
import EnhancedCourseOutline from '../components/EnhancedCourseOutline';
import CourseAssignments from '../components/CourseAssignments';
import CourseTutorChat from '../components/CourseTutorChat';
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import axios from 'axios';
import { toast } from "sonner";

// Generate FAQs based on course title, description, and faqs_references for service courses
function generateServiceFAQs(courseTitle, courseDescription, faqsReferences) {
  const title = courseTitle?.toLowerCase() || '';
  const desc = courseDescription?.toLowerCase() || '';
  const refs = faqsReferences?.toLowerCase() || '';
  
  // If faqs_references is provided, generate FAQs based on that content
  if (faqsReferences && faqsReferences.trim().length > 0) {
    return generateFAQsFromReferences(courseTitle, courseDescription, faqsReferences);
  }
  
  // Customer Service FAQs
  if (title.includes('customer service') || title.includes('customer support') || desc.includes('customer')) {
    return [
      { question: "What is customer service?", answer: "Customer service is the support and assistance provided to customers before, during, and after purchasing a product or service. It involves addressing inquiries, resolving issues, and ensuring customer satisfaction." },
      { question: "What skills are needed for customer service?", answer: "Key skills include communication, patience, empathy, problem-solving, active listening, product knowledge, time management, and the ability to remain calm under pressure." },
      { question: "How do I handle an angry customer?", answer: "Stay calm and listen actively, acknowledge their frustration, apologize sincerely, find a solution, follow up to ensure satisfaction, and learn from the experience to prevent similar issues." },
      { question: "What are the different channels of customer service?", answer: "Common channels include phone support, email, live chat, social media, self-service portals, in-person support, and video calls. Each channel has its own best practices." },
      { question: "How do I measure customer service success?", answer: "Key metrics include Customer Satisfaction Score (CSAT), Net Promoter Score (NPS), First Response Time, Resolution Time, Customer Effort Score, and Customer Retention Rate." },
      { question: "What is the importance of empathy in customer service?", answer: "Empathy helps you understand customer feelings and perspectives, builds trust, defuses tense situations, creates emotional connections, and leads to better problem resolution." }
    ];
  }
  
  // Healthcare/Medical Service FAQs
  if (title.includes('healthcare') || title.includes('medical') || title.includes('patient') || desc.includes('health')) {
    return [
      { question: "What is patient-centered care?", answer: "Patient-centered care is an approach that puts patients at the center of healthcare decisions, respecting their preferences, needs, and values while ensuring they guide all clinical decisions." },
      { question: "How do I maintain patient confidentiality?", answer: "Follow HIPAA guidelines, secure patient records, only share information on a need-to-know basis, use secure communication channels, and always verify identity before sharing information." },
      { question: "What are the key communication skills in healthcare?", answer: "Essential skills include active listening, clear explanations, empathy, non-verbal communication, cultural sensitivity, and the ability to explain complex medical terms simply." },
      { question: "How do I handle difficult patient situations?", answer: "Remain calm and professional, listen actively, acknowledge their concerns, involve supervisors when needed, document everything, and focus on finding solutions." },
      { question: "What is the importance of teamwork in healthcare?", answer: "Teamwork ensures coordinated care, reduces errors, improves patient outcomes, enhances efficiency, supports knowledge sharing, and creates a better work environment." },
      { question: "How do I manage stress in a healthcare environment?", answer: "Practice self-care, set boundaries, seek support from colleagues, use stress management techniques, take breaks, and consider professional counseling if needed." }
    ];
  }
  
  // Hospitality Service FAQs
  if (title.includes('hospitality') || title.includes('hotel') || title.includes('restaurant') || desc.includes('hospitality')) {
    return [
      { question: "What is hospitality service excellence?", answer: "Service excellence in hospitality means consistently exceeding guest expectations through personalized attention, anticipating needs, maintaining high standards, and creating memorable experiences." },
      { question: "How do I handle guest complaints?", answer: "Listen actively, apologize sincerely, take ownership, offer immediate solutions, follow up to ensure satisfaction, and use feedback to improve services." },
      { question: "What are the key qualities of a hospitality professional?", answer: "Key qualities include friendliness, attention to detail, professionalism, cultural awareness, problem-solving abilities, stamina, and genuine care for guest comfort." },
      { question: "How do I provide personalized guest experiences?", answer: "Remember guest preferences, use their names, anticipate needs, offer tailored recommendations, celebrate special occasions, and maintain detailed guest profiles." },
      { question: "What is the importance of first impressions in hospitality?", answer: "First impressions set the tone for the entire guest experience, influence satisfaction levels, affect reviews and recommendations, and can make or break return visits." },
      { question: "How do I maintain service consistency?", answer: "Follow standard operating procedures, participate in regular training, communicate with team members, use checklists, and continuously seek feedback for improvement." }
    ];
  }
  
  // Technical Support FAQs
  if (title.includes('technical') || title.includes('tech support') || title.includes('it support') || desc.includes('technical')) {
    return [
      { question: "What is technical support?", answer: "Technical support involves helping users solve problems with technology products or services, including troubleshooting, guidance, and resolving technical issues." },
      { question: "How do I troubleshoot effectively?", answer: "Start by gathering information, identify the problem clearly, check common issues first, use systematic elimination, document your steps, and escalate when necessary." },
      { question: "What communication skills are needed for tech support?", answer: "Essential skills include explaining technical concepts simply, patience with non-technical users, active listening, clear written communication, and knowing when to ask clarifying questions." },
      { question: "How do I handle users with limited technical knowledge?", answer: "Avoid jargon, use analogies, be patient, provide step-by-step instructions, confirm understanding at each step, and never make users feel embarrassed about their questions." },
      { question: "What is a ticketing system and why is it important?", answer: "A ticketing system tracks support requests, ensures no issues are lost, enables team collaboration, provides metrics for improvement, and maintains a history of solutions." },
      { question: "How do I stay updated with technology changes?", answer: "Participate in training, read documentation, follow industry news, join professional communities, practice with new technologies, and learn from colleagues." }
    ];
  }
  
  // Generic Service FAQs (fallback)
  return [
    { question: "What makes excellent service?", answer: "Excellent service combines professionalism, responsiveness, empathy, knowledge, and going above expectations. It's about understanding needs and delivering consistent, high-quality experiences." },
    { question: "How do I develop strong communication skills?", answer: "Practice active listening, be clear and concise, adapt your style to your audience, ask clarifying questions, use positive language, and seek feedback on your communication." },
    { question: "What is the importance of professionalism?", answer: "Professionalism builds trust, establishes credibility, creates positive impressions, maintains standards, and reflects well on both you and your organization." },
    { question: "How do I handle difficult situations?", answer: "Stay calm, listen actively, acknowledge concerns, focus on solutions, know when to escalate, document everything, and learn from each experience." },
    { question: "What are key service industry skills?", answer: "Key skills include communication, problem-solving, empathy, time management, adaptability, teamwork, attention to detail, and stress management." },
    { question: "How do I maintain a positive attitude?", answer: "Focus on what you can control, practice gratitude, take breaks when needed, celebrate small wins, seek support from colleagues, and remember the impact you have on others." },
    { question: "What is the role of feedback in service improvement?", answer: "Feedback helps identify strengths and weaknesses, guides training needs, improves processes, increases customer satisfaction, and drives continuous improvement." },
    { question: "How do I build rapport with customers/clients?", answer: "Use their name, find common ground, show genuine interest, remember details from previous interactions, be authentic, and follow through on promises." }
  ];
}

// Generate FAQs from the faqs_references content in the database
function generateFAQsFromReferences(courseTitle, courseDescription, faqsReferences) {
  const faqs = [];
  const title = courseTitle || 'Service';
  const description = courseDescription || '';
  
  // Parse the faqs_references - it can be structured as key points or topics
  // Split by common delimiters: newlines, semicolons, or numbered lists
  const referenceLines = faqsReferences
    .split(/[\n;]|(?:\d+\.\s)/)
    .map(line => line.trim())
    .filter(line => line.length > 10); // Filter out very short lines
  
  // Generate FAQs based on reference content
  referenceLines.forEach((reference, index) => {
    if (index < 8) { // Limit to 8 FAQs from references
      // Generate a question based on the reference topic
      const question = generateQuestionFromReference(reference, title);
      const answer = generateAnswerFromReference(reference, description);
      
      if (question && answer) {
        faqs.push({ question, answer });
      }
    }
  });
  
  // If we have references but couldn't generate enough FAQs, add suggested FAQs
  if (faqs.length < 4) {
    const suggestedFAQs = generateSuggestedFAQs(courseTitle, courseDescription, faqsReferences);
    suggestedFAQs.forEach(faq => {
      if (faqs.length < 8 && !faqs.some(f => f.question.toLowerCase() === faq.question.toLowerCase())) {
        faqs.push(faq);
      }
    });
  }
  
  return faqs.length > 0 ? faqs : generateSuggestedFAQs(courseTitle, courseDescription, faqsReferences);
}

// Generate a question from a reference topic
function generateQuestionFromReference(reference, courseTitle) {
  const ref = reference.toLowerCase();
  
  // Pattern matching for common question types
  if (ref.includes('how to') || ref.includes('process') || ref.includes('step')) {
    return `How do I ${extractKeyTopic(reference)}?`;
  }
  if (ref.includes('what is') || ref.includes('definition') || ref.includes('meaning')) {
    return `What is ${extractKeyTopic(reference)}?`;
  }
  if (ref.includes('why') || ref.includes('important') || ref.includes('benefit')) {
    return `Why is ${extractKeyTopic(reference)} important?`;
  }
  if (ref.includes('when') || ref.includes('timing') || ref.includes('schedule')) {
    return `When should I ${extractKeyTopic(reference)}?`;
  }
  if (ref.includes('policy') || ref.includes('rule') || ref.includes('guideline')) {
    return `What are the guidelines for ${extractKeyTopic(reference)}?`;
  }
  if (ref.includes('contact') || ref.includes('reach') || ref.includes('support')) {
    return `How can I get help with ${extractKeyTopic(reference)}?`;
  }
  if (ref.includes('cost') || ref.includes('price') || ref.includes('fee') || ref.includes('payment')) {
    return `What are the costs associated with ${extractKeyTopic(reference)}?`;
  }
  if (ref.includes('require') || ref.includes('need') || ref.includes('document')) {
    return `What do I need to know about ${extractKeyTopic(reference)}?`;
  }
  
  // Default question format
  return `What should I know about ${extractKeyTopic(reference)}?`;
}

// Extract the key topic from a reference
function extractKeyTopic(reference) {
  // Clean up the reference and extract the main topic
  let topic = reference
    .replace(/^(how to|what is|why|when|the|a|an)\s+/i, '')
    .replace(/[?.!,;:]+$/, '')
    .trim();
  
  // Limit length
  if (topic.length > 60) {
    topic = topic.substring(0, 60).trim() + '...';
  }
  
  return topic.toLowerCase();
}

// Generate an answer from a reference
function generateAnswerFromReference(reference, description) {
  // The answer is based on the reference content, expanded with context
  let answer = reference.trim();
  
  // If the reference is too short, expand it
  if (answer.length < 50) {
    answer = `${answer}. This is an important aspect of the service that helps ensure quality and satisfaction.`;
  }
  
  // Clean up and format
  answer = answer.charAt(0).toUpperCase() + answer.slice(1);
  if (!answer.endsWith('.') && !answer.endsWith('!') && !answer.endsWith('?')) {
    answer += '.';
  }
  
  return answer;
}

// Generate suggested FAQs based on course context
function generateSuggestedFAQs(courseTitle, courseDescription, faqsReferences) {
  const title = courseTitle?.toLowerCase() || '';
  const desc = courseDescription?.toLowerCase() || '';
  const refs = faqsReferences?.toLowerCase() || '';
  const combined = `${title} ${desc} ${refs}`;
  
  const suggestedFAQs = [];
  
  // Add FAQs based on detected topics in the references
  if (combined.includes('appointment') || combined.includes('booking') || combined.includes('schedule')) {
    suggestedFAQs.push({
      question: "How do I schedule an appointment or booking?",
      answer: "To schedule an appointment, contact our service desk or use our online booking system. Provide your preferred date and time, and our team will confirm availability and send you a confirmation."
    });
  }
  
  if (combined.includes('cancel') || combined.includes('refund') || combined.includes('return')) {
    suggestedFAQs.push({
      question: "What is the cancellation or refund policy?",
      answer: "Our cancellation policy allows for changes up to 24 hours before the scheduled service. Refunds are processed according to our terms of service. Please contact support for specific cases."
    });
  }
  
  if (combined.includes('hours') || combined.includes('timing') || combined.includes('open')) {
    suggestedFAQs.push({
      question: "What are the service hours?",
      answer: "Our service hours vary by location and service type. Generally, we operate during standard business hours. Check with your specific service provider for exact timing."
    });
  }
  
  if (combined.includes('contact') || combined.includes('support') || combined.includes('help')) {
    suggestedFAQs.push({
      question: "How can I contact support?",
      answer: "You can reach our support team through multiple channels: phone, email, live chat, or in-person visits. Our AI assistant is also available 24/7 to help answer your questions."
    });
  }
  
  if (combined.includes('document') || combined.includes('require') || combined.includes('bring')) {
    suggestedFAQs.push({
      question: "What documents or information do I need to provide?",
      answer: "Required documents vary by service. Generally, you may need identification, relevant forms, and any supporting documentation specific to your request. Contact us for a detailed checklist."
    });
  }
  
  if (combined.includes('wait') || combined.includes('time') || combined.includes('long')) {
    suggestedFAQs.push({
      question: "How long does the service typically take?",
      answer: "Service duration depends on the complexity of your request. Simple inquiries may be resolved immediately, while more complex matters may require additional time. We strive to be efficient while ensuring quality."
    });
  }
  
  if (combined.includes('complaint') || combined.includes('feedback') || combined.includes('issue')) {
    suggestedFAQs.push({
      question: "How do I submit feedback or a complaint?",
      answer: "We value your feedback! You can submit comments through our feedback form, email, or speak directly with a supervisor. All feedback is reviewed and helps us improve our services."
    });
  }
  
  if (combined.includes('language') || combined.includes('translate') || combined.includes('interpreter')) {
    suggestedFAQs.push({
      question: "Are services available in other languages?",
      answer: "We offer multilingual support for many of our services. Interpreter services may be available upon request. Please let us know your language preference when scheduling."
    });
  }
  
  // Add general suggested FAQs if we don't have enough
  if (suggestedFAQs.length < 4) {
    suggestedFAQs.push({
      question: `What services are included in ${courseTitle || 'this program'}?`,
      answer: `${courseDescription || 'This program includes comprehensive training and resources to help you succeed. Our services are designed to meet your specific needs and goals.'}`
    });
    
    suggestedFAQs.push({
      question: "Who can I speak to for more information?",
      answer: "Our trained staff and AI assistants are available to answer your questions. For complex inquiries, we can connect you with a specialist who can provide detailed guidance."
    });
    
    suggestedFAQs.push({
      question: "Are there any prerequisites or requirements?",
      answer: "Requirements vary depending on the specific service. Generally, we aim to make our services accessible to everyone. Contact us to discuss your specific situation."
    });
    
    suggestedFAQs.push({
      question: "How can I prepare for my service appointment?",
      answer: "To make the most of your appointment, gather any relevant documents, prepare your questions in advance, and arrive a few minutes early. Our staff will guide you through the process."
    });
  }
  
  return suggestedFAQs.slice(0, 8);
}

// Smart topic generator based on course title and type
function generateCourseTopics(courseTitle, courseType) {
  const title = courseTitle?.toLowerCase() || '';
  
  // Programming & Computer Science courses
  if (title.includes('programming') || title.includes('java') || title.includes('python') || title.includes('code')) {
    return [
      { name: "Introduction to Programming Concepts", description: "Learn fundamental programming logic, variables, and data types" },
      { name: "Control Flow and Decision Making", description: "Master if statements, loops, and conditional logic" },
      { name: "Functions and Modular Programming", description: "Write reusable code with functions and parameters" },
      { name: "Data Structures Fundamentals", description: "Understand arrays, lists, and basic data organization" },
      { name: "Object-Oriented Programming", description: "Learn classes, objects, inheritance, and polymorphism" },
      { name: "File Handling and Input/Output", description: "Work with files, read/write operations, and data persistence" },
      { name: "Error Handling and Debugging", description: "Handle exceptions and debug your code effectively" },
      { name: "Final Project and Best Practices", description: "Build a complete application using everything you've learned" }
    ];
  }
  
  // Data Structures & Algorithms
  if (title.includes('data structure') || title.includes('algorithm')) {
    return [
      { name: "Algorithm Analysis and Big O Notation", description: "Understand time and space complexity analysis" },
      { name: "Arrays and Linked Lists", description: "Master sequential data structures and operations" },
      { name: "Stacks and Queues", description: "Learn LIFO and FIFO data structures with applications" },
      { name: "Trees and Binary Search Trees", description: "Understand hierarchical data structures and tree traversals" },
      { name: "Graphs and Graph Algorithms", description: "Explore graph representations, BFS, DFS, and shortest paths" },
      { name: "Hashing and Hash Tables", description: "Implement efficient key-value storage and collision handling" },
      { name: "Sorting and Searching Algorithms", description: "Master quicksort, mergesort, binary search, and more" },
      { name: "Dynamic Programming", description: "Solve complex problems with memoization and optimization" }
    ];
  }
  
  // Data Science & Machine Learning
  if (title.includes('data science') || title.includes('machine learning') || title.includes('ml') || title.includes('ai')) {
    return [
      { name: "Introduction to Data Science", description: "Overview of data science workflow and tools" },
      { name: "Python for Data Analysis", description: "Master NumPy, Pandas, and data manipulation" },
      { name: "Data Visualization", description: "Create insightful visualizations with Matplotlib and Seaborn" },
      { name: "Statistical Analysis Fundamentals", description: "Understand probability, distributions, and hypothesis testing" },
      { name: "Machine Learning Basics", description: "Learn supervised and unsupervised learning concepts" },
      { name: "Regression and Classification", description: "Build predictive models for various problems" },
      { name: "Model Evaluation and Optimization", description: "Evaluate performance and fine-tune your models" },
      { name: "Real-World Data Science Projects", description: "Apply your skills to solve practical problems" }
    ];
  }
  
  // Web Development
  if (title.includes('web') || title.includes('html') || title.includes('css') || title.includes('react')) {
    return [
      { name: "HTML Fundamentals", description: "Build webpage structure with semantic HTML5" },
      { name: "CSS Styling and Layouts", description: "Style pages with modern CSS and Flexbox/Grid" },
      { name: "JavaScript Essentials", description: "Add interactivity with JavaScript fundamentals" },
      { name: "DOM Manipulation", description: "Control webpage elements dynamically" },
      { name: "Modern JavaScript (ES6+)", description: "Use arrow functions, promises, and async/await" },
      { name: "React Framework Basics", description: "Build component-based user interfaces" },
      { name: "API Integration", description: "Connect to backend services and handle data" },
      { name: "Deployment and Best Practices", description: "Deploy your web applications and optimize performance" }
    ];
  }
  
  // Interview & Career Skills
  if (title.includes('interview') || title.includes('career') || title.includes('job')) {
    return [
      { name: "Resume and CV Writing", description: "Create professional resumes that get noticed" },
      { name: "Cover Letter Techniques", description: "Write compelling cover letters for applications" },
      { name: "Interview Preparation Strategies", description: "Prepare effectively for different interview types" },
      { name: "Common Interview Questions", description: "Practice answering behavioral and technical questions" },
      { name: "Body Language and Communication", description: "Master non-verbal communication and professional presence" },
      { name: "Salary Negotiation", description: "Learn to negotiate compensation confidently" },
      { name: "Mock Interviews and Feedback", description: "Practice with AI-powered interview simulations" },
      { name: "Post-Interview Follow-up", description: "Handle follow-ups and job offers professionally" }
    ];
  }
  
  // Presentation & Communication
  if (title.includes('presentation') || title.includes('public speaking') || title.includes('communication')) {
    return [
      { name: "Presentation Fundamentals", description: "Learn the basics of effective presentations" },
      { name: "Structuring Your Content", description: "Organize ideas for maximum impact" },
      { name: "Visual Design Principles", description: "Create engaging and professional slides" },
      { name: "Public Speaking Techniques", description: "Master vocal variety, pacing, and clarity" },
      { name: "Body Language and Stage Presence", description: "Project confidence through non-verbal communication" },
      { name: "Handling Q&A Sessions", description: "Respond to questions effectively and professionally" },
      { name: "Virtual Presentations", description: "Excel at online presentations and video meetings" },
      { name: "Senior Project Defense Preparation", description: "Defend your work confidently in academic settings" }
    ];
  }
  
  // Language Learning
  if (title.includes('language') || title.includes('speak') || title.includes('conversation')) {
    return [
      { name: "Introduction and Basic Phrases", description: "Start speaking with essential greetings and expressions" },
      { name: "Grammar Fundamentals", description: "Understand core grammar rules and sentence structure" },
      { name: "Vocabulary Building", description: "Expand your word bank with practical vocabulary" },
      { name: "Pronunciation and Accent", description: "Improve your pronunciation for clear communication" },
      { name: "Conversational Practice", description: "Practice real-life conversations with AI tutor" },
      { name: "Reading and Comprehension", description: "Develop reading skills and understand texts" },
      { name: "Writing Skills", description: "Learn to write clearly and effectively" },
      { name: "Cultural Context and Idioms", description: "Understand cultural nuances and common expressions" }
    ];
  }
  
  // General/Default topics for any other course
  return [
    { name: "Course Introduction and Overview", description: "Get familiar with course objectives and learning path" },
    { name: "Core Concepts and Fundamentals", description: "Build strong foundation in key concepts" },
    { name: "Intermediate Techniques", description: "Develop practical skills and apply your knowledge" },
    { name: "Advanced Topics", description: "Explore complex concepts and advanced applications" },
    { name: "Hands-on Practice", description: "Work on exercises and practical examples" },
    { name: "Real-World Applications", description: "See how concepts apply to real scenarios" },
    { name: "Final Project", description: "Build a complete project showcasing your skills" },
    { name: "Review and Best Practices", description: "Consolidate learning and prepare for next steps" }
  ];
}

export default function DynamicCoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isTeacher, setIsTeacher] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const token = localStorage.getItem('faheem_token');

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        
        // Fetch course data and check teacher status in parallel
        const coursePromise = axios.get(`${BACKEND_URL}/api/courses/`);
        const teacherPromise = token 
          ? axios.get(`${BACKEND_URL}/api/teacher/check?access_token=${token}`).catch(() => ({ data: { is_teacher: false } }))
          : Promise.resolve({ data: { is_teacher: false } });
        
        const [courseResponse, teacherResponse] = await Promise.all([coursePromise, teacherPromise]);
        
        // Set teacher status
        setIsTeacher(teacherResponse.data?.is_teacher || false);
        
        const courses = courseResponse.data?.courses || [];
        
        // Find the specific course by ID
        const foundCourse = courses.find(c => c.id === courseId);
        
        if (!foundCourse) {
          setError(`Course with ID "${courseId}" not found`);
          return;
        }
        
        setCourse(foundCourse);
      } catch (err) {
        console.error('Error fetching course:', err);
        setError('Failed to load course data. Please try again later.');
        toast.error('Failed to load course');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId, BACKEND_URL, token]);

  // Get color class based on course color
  const getColorClass = (color) => {
    const colorMap = {
      blue: 'from-blue-500 to-blue-600',
      purple: 'from-purple-500 to-purple-600',
      green: 'from-green-500 to-green-600',
      orange: 'from-orange-500 to-orange-600',
      teal: 'from-teal-500 to-teal-600',
      red: 'from-red-500 to-red-600',
      indigo: 'from-indigo-500 to-indigo-600',
      pink: 'from-pink-500 to-pink-600',
    };
    return colorMap[color?.toLowerCase()] || 'from-blue-500 to-blue-600';
  };

  // Get icon component color
  const getIconColor = (color) => {
    const colorMap = {
      blue: 'text-blue-600',
      purple: 'text-purple-600',
      green: 'text-green-600',
      orange: 'text-orange-600',
      teal: 'text-teal-600',
      red: 'text-red-600',
      indigo: 'text-indigo-600',
      pink: 'text-pink-600',
      cyan: 'text-cyan-600',
    };
    return colorMap[color?.toLowerCase()] || 'text-blue-600';
  };

  // Get background color class
  const getBgColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-100',
      purple: 'bg-purple-100',
      green: 'bg-green-100',
      orange: 'bg-orange-100',
      teal: 'bg-teal-100',
      red: 'bg-red-100',
      indigo: 'bg-indigo-100',
      pink: 'bg-pink-100',
      cyan: 'bg-cyan-100',
    };
    return colorMap[color?.toLowerCase()] || 'bg-blue-100';
  };

  // Get badge color class
  const getBadgeColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-700',
      purple: 'bg-purple-100 text-purple-700',
      green: 'bg-green-100 text-green-700',
      orange: 'bg-orange-100 text-orange-700',
      teal: 'bg-teal-100 text-teal-700',
      red: 'bg-red-100 text-red-700',
      indigo: 'bg-indigo-100 text-indigo-700',
      pink: 'bg-pink-100 text-pink-700',
      cyan: 'bg-cyan-100 text-cyan-700',
    };
    return colorMap[color?.toLowerCase()] || 'bg-blue-100 text-blue-700';
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading course...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !course) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Course Not Found</h2>
              <p className="text-slate-600 mb-6">{error || 'The course you are looking for does not exist.'}</p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate('/courses')} variant="default">
                  Browse Courses
                </Button>
                <Button onClick={() => navigate('/')} variant="outline">
                  Go Home
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if course is coming soon - render coming soon page instead
  if (course.status === 'coming_soon') {
    return <ComingSoonTemplate course={course} />;
  }

  // Parse topics from course data for active courses
  let courseTopics = [];
  if (course.topics) {
    courseTopics = Array.isArray(course.topics) ? course.topics : 
                   typeof course.topics === 'string' ? JSON.parse(course.topics) : [];
  }

  // Generate course-specific topics if none exist
  if (courseTopics.length === 0) {
    courseTopics = generateCourseTopics(course.title, course.type);
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      
      {/* Top Navigation Bar - Clean & Minimal */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to={isTeacher ? "/teacher-profile" : "/profile"}>
                <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900 gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back</span>
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-200 hidden sm:block" />
              <div className="flex items-center gap-3">
                <div className={`bg-gradient-to-br ${getColorClass(course.color)} p-2 rounded-lg shadow-sm`}>
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-sm sm:text-base font-bold text-slate-800 leading-tight">{course.title}</h1>
                  <p className="text-xs text-slate-500 hidden sm:block">{course.level || 'All Levels'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SessionRecorder courseId={course.id} courseTitle={course.title} />
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-100 rounded-full text-xs font-medium text-green-700">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                <span className="hidden sm:inline">Online</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        
        {/* Course Header Section - Dark Blue Theme */}
        <div className="mb-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-white/20 text-white hover:bg-white/30 text-xs">
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI-Powered Course
                    </Badge>
                    {course.status === 'active' && (
                      <Badge className="bg-green-500/20 text-green-300 hover:bg-green-500/30 text-xs">
                        Active
                      </Badge>
                    )}
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-white mb-2">{course.title}</h2>
                  <p className="text-white/80 text-sm max-w-2xl leading-relaxed">{course.description}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="bg-white/10 border-white/30 text-white">
                    {course.level || 'All Levels'}
                  </Badge>
                  <Badge variant="outline" className="bg-white/10 border-white/30 text-white">
                    {courseTopics.length} Topics
                  </Badge>
                  <Badge variant="outline" className="bg-white/10 border-white/30 text-white">
                    {course.modules || 0} Modules
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Layout - 50/50 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Left Column - Course Content (50% width) */}
          <div className="space-y-6">
            
            {/* Conditionally render content based on course type */}
            {course.type?.toLowerCase() === 'service' ? (
              <>
                {/* FAQ Section for Service Courses */}
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader className="pb-3 pt-5 px-5 border-b border-slate-100">
                    <CardTitle className="text-base font-bold flex items-center gap-3 text-slate-800">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${getBgColorClass(course.color)}`}>
                        <HelpCircle className={`w-5 h-5 ${getIconColor(course.color)}`} />
                      </div>
                      Frequently Asked Questions
                    </CardTitle>
                    <p className="text-sm text-slate-500 mt-1 ml-12">
                      Common questions and answers to help you learn
                    </p>
                  </CardHeader>
                  <CardContent className="px-5 py-4">
                    <Accordion type="single" collapsible className="space-y-2">
                      {generateServiceFAQs(course.title, course.description, course.faqs_references).map((faq, index) => (
                        <AccordionItem 
                          key={index} 
                          value={`faq-${index}`} 
                          className="border rounded-xl px-4 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                        >
                          <AccordionTrigger className="hover:no-underline py-4 text-left">
                            <div className="flex items-start gap-3 flex-1 pr-2">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${getBadgeColorClass(course.color)}`}>
                                {index + 1}
                              </span>
                              <span className="text-sm font-medium text-slate-800 leading-relaxed">
                                {faq.question}
                              </span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pb-4 pt-0">
                            <div className="ml-9 text-sm text-slate-600 leading-relaxed bg-white rounded-lg p-4 border border-slate-100">
                              {faq.answer}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                {/* Enhanced Course Outline */}
                <EnhancedCourseOutline
                  courseId={course.id}
                  courseTitle={course.title}
                  topics={courseTopics}
                />

                {/* Assignments & Worksheets */}
                <CourseAssignments courseId={course.id} />
              </>
            )}

            {/* Course Features - Dark Blue Theme */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-300">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-900">
                      <Mic className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Voice Enabled</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        Hands-free interaction. Just speak naturally to ask questions.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-all duration-300">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-slate-900">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Smart AI Responses</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        Powered by advanced AI to provide accurate help and guidance.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Column - AI Tutor (50% width) - Sticky */}
          <div className="lg:sticky lg:top-20 lg:h-fit">
            <div>
              {/* AI Tutor Card - Dark Blue Theme */}
              <Card className="bg-white border-slate-200 shadow-xl overflow-hidden">
                {/* Avatar Header */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm">FaheemGPT Tutor</h3>
                      <p className="text-white/70 text-xs">Ready to help you learn</p>
                    </div>
                    <div className="ml-auto flex items-center gap-1 bg-green-500/20 px-2 py-1 rounded-full">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400"></span>
                      </span>
                      <span className="text-xs text-green-300">Live</span>
                    </div>
                  </div>
                </div>
                
                {/* Window Controls */}
                <div className="h-7 bg-slate-50 border-b border-slate-100 flex items-center px-3 gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400/80"></div>
                  </div>
                  <div className="mx-auto text-[10px] font-medium text-slate-400 uppercase tracking-wide">
                    AI Assistant
                  </div>
                </div>

                {/* The D-ID Avatar Container - Optimized Height */}
                <div className="relative w-full h-[380px] lg:h-[420px] bg-slate-100 overflow-hidden">
                  <DidEmbed agentId={course.avatar_code || course.agent_id || "v2_agt__F8gQmS3"} />
                </div>

                {/* Quick Actions */}
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                  <p className="text-xs text-slate-500 text-center mb-3">
                    Click on the avatar or use the chat to ask questions
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 text-xs">
                      <MessageSquare className="w-3 h-3 mr-1.5" />
                      Ask a Question
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1 text-xs">
                      <Target className="w-3 h-3 mr-1.5" />
                      Practice Quiz
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          </div>

        </div>
      </main>
      
      {/* Floating AI Tutor Chat */}
      <CourseTutorChat courseId={course.id} courseName={course.title} />
      
      <Toaster />
    </div>
  );
}

// Coming Soon Template Component
function ComingSoonTemplate({ course }) {
  // Get gradient colors based on course color
  const getGradientClass = (color) => {
    const colorMap = {
      blue: 'from-blue-600 to-indigo-600',
      purple: 'from-purple-600 to-pink-600',
      green: 'from-green-600 to-emerald-600',
      orange: 'from-orange-600 to-red-600',
      teal: 'from-teal-600 to-cyan-600',
      red: 'from-red-600 to-rose-600',
      indigo: 'from-indigo-600 to-purple-600',
      pink: 'from-pink-600 to-rose-600',
    };
    return colorMap[color?.toLowerCase()] || 'from-blue-600 to-indigo-600';
  };

  // Get background decoration color
  const getBgColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-50',
      purple: 'bg-purple-50',
      green: 'bg-green-50',
      orange: 'bg-orange-50',
      teal: 'bg-teal-50',
      red: 'bg-red-50',
      indigo: 'bg-indigo-50',
      pink: 'bg-pink-50',
    };
    return colorMap[color?.toLowerCase()] || 'bg-blue-50';
  };

  // Get badge color
  const getBadgeColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-700',
      purple: 'bg-purple-100 text-purple-700',
      green: 'bg-green-100 text-green-700',
      orange: 'bg-orange-100 text-orange-700',
      teal: 'bg-teal-100 text-teal-700',
      red: 'bg-red-100 text-red-700',
      indigo: 'bg-indigo-100 text-indigo-700',
      pink: 'bg-pink-100 text-pink-700',
    };
    return colorMap[color?.toLowerCase()] || 'bg-blue-100 text-blue-700';
  };

  // Get feature icons based on course type
  const getFeatureIcons = () => {
    const type = course.type?.toLowerCase();
    if (type === 'life-skills' || type === 'skills') {
      return [
        { icon: MessageSquare, title: 'Interactive Practice', desc: 'Practice with real-time AI conversations and feedback.' },
        { icon: Target, title: 'Personalized Learning', desc: 'Adaptive lessons that match your skill level and goals.' },
        { icon: Zap, title: 'Instant Feedback', desc: 'Get immediate guidance and corrections as you learn.' },
      ];
    }
    return [
      { icon: Code2, title: 'Hands-On Learning', desc: 'Practice with real examples and interactive exercises.' },
      { icon: Brain, title: 'AI-Powered Tutoring', desc: 'Get personalized help from your intelligent assistant.' },
      { icon: Zap, title: 'Master Concepts', desc: 'Build strong foundations with step-by-step guidance.' },
    ];
  };

  const features = getFeatureIcons();

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-opacity-10 flex flex-col">
      
      {/* Navbar - Compact */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Link to="/">
                <img 
                  src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
                  alt="FaheemGPT Logo" 
                  className="w-10 h-10 rounded-lg object-cover" 
                />
            </Link>
            <span className="text-xl font-bold tracking-tight">FaheemGPT <span className="text-slate-400 font-normal">Intelligent Tutoring Platform</span></span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/">
                <Button variant="ghost" className="flex gap-2">
                    <ArrowLeft className="w-4 h-4" /> Back to Home
                </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center relative overflow-hidden px-6 py-20">
        
        {/* Background Decorations */}
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] ${getBgColorClass(course.color)} rounded-full blur-3xl -z-10 opacity-50 animate-pulse-slow`}></div>
        <div className="absolute top-20 right-20 w-32 h-32 bg-blue-100 rounded-full blur-2xl -z-10 opacity-60"></div>
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-indigo-100 rounded-full blur-2xl -z-10 opacity-60"></div>
        
        <div className="max-w-3xl mx-auto text-center space-y-8">
            <Badge className={`${getBadgeColorClass(course.color)} hover:${getBadgeColorClass(course.color)} border-none px-4 py-1.5 text-sm`}>
                <Rocket className="w-4 h-4 mr-2 inline" /> {course.semester || 'Launching Soon'}
            </Badge>
            
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900">
                {course.title?.split(' ').slice(0, -1).join(' ')} <br/>
                <span className={`text-transparent bg-clip-text bg-gradient-to-r ${getGradientClass(course.color)}`}>
                  {course.title?.split(' ').slice(-1)[0] || 'Coming Soon'}
                </span>
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
                {course.description}
            </p>

            {/* Course Info Badges */}
            <div className="flex gap-3 justify-center flex-wrap">
              {course.level && (
                <Badge variant="outline" className="px-4 py-1.5">
                  {course.level}
                </Badge>
              )}
              {course.modules && (
                <Badge variant="outline" className="px-4 py-1.5">
                  {course.modules} Modules
                </Badge>
              )}
              {course.instructor && (
                <Badge variant="outline" className="px-4 py-1.5">
                  By {course.instructor}
                </Badge>
              )}
            </div>

            {/* Instructor Video - if available */}
            {course.video_url && (
              <div className="pt-8 max-w-2xl mx-auto">
                  <div className="relative aspect-video rounded-xl overflow-hidden shadow-xl border-4 border-white bg-slate-900">
                      <video 
                          src={course.video_url}
                          className="w-full h-full object-contain"
                          controls
                          playsInline
                      />
                      <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-md text-white text-sm font-medium">
                          Meet Your Instructor
                      </div>
                  </div>
              </div>
            )}

            {/* Feature Preview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 text-left">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  const colors = ['border-blue-200', 'border-purple-200', 'border-green-200'];
                  const bgColors = ['bg-blue-100', 'bg-purple-100', 'bg-green-100'];
                  const textColors = ['text-blue-600', 'text-purple-600', 'text-green-600'];
                  
                  return (
                    <Card key={index} className={`bg-white border-slate-200 shadow-sm hover:${colors[index]} transition-colors`}>
                        <CardContent className="p-6 space-y-3">
                            <div className={`w-10 h-10 ${bgColors[index]} rounded-lg flex items-center justify-center ${textColors[index]}`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <h3 className="font-semibold text-slate-900">{feature.title}</h3>
                            <p className="text-sm text-slate-500">{feature.desc}</p>
                        </CardContent>
                    </Card>
                  );
                })}
            </div>

            {/* CTA */}
            <div className="pt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button size="lg" className="h-14 px-8 text-lg bg-slate-900 text-white hover:bg-slate-800 rounded-full shadow-lg">
                    <Bell className="w-5 h-5 mr-2" />
                    Notify Me When Ready
                </Button>
                <Link to="/login">
                    <Button variant="outline" size="lg" className="h-14 px-8 text-lg rounded-full">
                        Login to Explore Other Courses
                    </Button>
                </Link>
            </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
              alt="FaheemGPT Logo" 
              className="w-8 h-8 rounded-lg object-cover" 
            />
             <span className="font-semibold text-slate-700">FaheemGPT Intelligent Tutoring Platform</span>
          </div>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/support" className="hover:text-slate-900">Support</Link>
          </div>
          <div>
            &copy; 2024 FaheemGPT Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}

// Helper function for level badge colors
function getLevelBadgeClass(level) {
  const levelMap = {
    'beginner': 'bg-blue-50 text-blue-700 border-blue-200',
    'intermediate': 'bg-purple-50 text-purple-700 border-purple-200',
    'advanced': 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return levelMap[level?.toLowerCase()] || 'bg-slate-50 text-slate-700 border-slate-200';
}
